import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Archive, Trash2, Edit3, Check, X, Loader2, ArchiveRestore,
  Undo2, Sparkles, Search, MoreVertical,
} from 'lucide-react';
import { haptic } from '../../../telegram';
import { listConversations, renameConversation, setArchived, deleteConversation } from '../../../api/chatApi';
import { ChatView } from './ChatView';

// Относительное время: «только что», «5 мин назад», «2 ч назад», «вчера», дата
function relTime(s: string) {
  if (!s) return '';
  const d = new Date(s);
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'только что';
  if (m < 60) return `${m} мин назад`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч назад`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'вчера';
  if (days < 7) return `${days} дн назад`;
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

function initial(title: string) { return (title || 'Д').trim().slice(0, 1).toUpperCase(); }

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#2E86FF 0%,#45C2FF 100%)',
  'linear-gradient(135deg,#7C5CFC 0%,#A78BFA 100%)',
  'linear-gradient(135deg,#F87171 0%,#FB923C 100%)',
  'linear-gradient(135deg,#34D399 0%,#10B981 100%)',
  'linear-gradient(135deg,#F59E0B 0%,#FBBF24 100%)',
  'linear-gradient(135deg,#EC4899 0%,#F472B6 100%)',
];
function avatarGrad(title: string) {
  let h = 0; const t = (title || 'Д');
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

type Toast = { text: string; onUndo?: () => void | Promise<void> };

export function Assistant() {
  const [convs, setConvs] = useState<any[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [renameVal, setRenameVal] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [query, setQuery] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingDeletesRef = useRef<Map<string, { timer: ReturnType<typeof setTimeout>; conv: any }>>(new Map());

  const load = (archived = false) => {
    setLoading(true);
    listConversations(archived)
      .then((r) => { setConvs(r || []); setLoading(false); })
      .catch(() => setLoading(false));
  };
  useEffect(() => { load(false); }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    pendingDeletesRef.current.forEach(({ timer }, id) => { clearTimeout(timer); deleteConversation(id).catch(() => {}); });
    pendingDeletesRef.current.clear();
  }, []);

  function showToast(text: string, onUndo?: () => void | Promise<void>) {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ text, onUndo });
    toastTimer.current = setTimeout(() => setToast(null), 5000);
  }
  function hideToast() { if (toastTimer.current) clearTimeout(toastTimer.current); setToast(null); }

  const switchTab = (archived: boolean) => { haptic('light'); setShowArchived(archived); setRenaming(null); setMenuFor(null); load(archived); };
  const newChat = () => { haptic('medium'); setActiveId('new'); };
  const startRename = (c: any) => { haptic('light'); setMenuFor(null); setRenaming(c.id); setRenameVal(c.title || ''); };

  const saveRename = async () => {
    if (!renaming) return;
    const id = renaming;
    const oldTitle = convs.find((c) => c.id === id)?.title || '';
    const newTitle = renameVal.trim();
    if (!newTitle || newTitle === oldTitle) { setRenaming(null); return; }
    haptic('medium'); setSavingRename(true);
    try {
      await renameConversation(id, newTitle);
      setConvs((p) => p.map((c) => (c.id === id ? { ...c, title: newTitle } : c)));
      setRenaming(null); haptic('heavy');
      showToast('Переименовано', async () => {
        try { await renameConversation(id, oldTitle); setConvs((p) => p.map((c) => (c.id === id ? { ...c, title: oldTitle } : c))); } catch {}
      });
    } catch {} finally { setSavingRename(false); }
  };

  const archive = async (id: string) => {
    haptic('heavy'); setMenuFor(null);
    const conv = convs.find((c) => c.id === id); if (!conv) return;
    const wasArchived = showArchived;
    setConvs((p) => p.filter((c) => c.id !== id));
    try { await setArchived(id, !wasArchived); } catch { setConvs((p) => [conv, ...p]); return; }
    showToast(wasArchived ? 'Восстановлено из архива' : 'Диалог в архиве', async () => {
      try { await setArchived(id, wasArchived); setConvs((p) => [conv, ...p]); } catch {}
    });
  };

  const del = async (id: string) => {
    haptic('heavy'); setMenuFor(null);
    const conv = convs.find((c) => c.id === id); if (!conv) return;
    setConvs((p) => p.filter((c) => c.id !== id));
    const timer = setTimeout(() => { pendingDeletesRef.current.delete(id); deleteConversation(id).catch(() => {}); }, 5000);
    pendingDeletesRef.current.set(id, { timer, conv });
    showToast('Диалог удалён', () => {
      const p = pendingDeletesRef.current.get(id);
      if (p) { clearTimeout(p.timer); pendingDeletesRef.current.delete(id); }
      setConvs((prev) => [conv, ...prev]);
    });
  };

  const onChatBack = (action?: { type: 'archived' | 'deleted'; id: string }) => {
    setActiveId(null); load(showArchived);
    if (action?.type === 'archived') showToast('Диалог в архиве', async () => { try { await setArchived(action.id, false); load(showArchived); } catch {} });
    if (action?.type === 'deleted') showToast('Диалог удалён');
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return convs;
    return convs.filter((c) =>
      (c.title || '').toLowerCase().includes(q) || (c.lastMessage || '').toLowerCase().includes(q));
  }, [convs, query]);

  if (activeId !== null) {
    return <ChatView conversationId={activeId === 'new' ? null : activeId} onBack={onChatBack} />;
  }

  return (
    <div className="rf-page rf-apage" onClick={() => menuFor && setMenuFor(null)}>
      <div className="rf-apage-intro oa-assistant-intro">
        <div>
          <h1 className="rf-apage-title">Ассистент</h1>
          <p className="rf-apage-desc">Опишите задачу словами — оркестратор подберёт агентов и соберёт сценарий.</p>
        </div>
        <button className="oa-new-chat" onClick={newChat}><Plus />Новый диалог</button>
      </div>

      <div className="rf-seg rf-in">
        <button className={'rf-seg-btn' + (!showArchived ? ' active' : '')} onClick={() => switchTab(false)}>Активные</button>
        <button className={'rf-seg-btn' + (showArchived ? ' active' : '')} onClick={() => switchTab(true)}>Архив</button>
      </div>

      {/* Поиск по истории */}
      <div className="rf-search rf-in">
        <Search />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Поиск по диалогам…" />
        {query && <button className="rf-search-x" onClick={() => setQuery('')}><X /></button>}
      </div>

      {loading ? (
        <div className="rf-loading rf-in"><div className="rf-spin" /><p>Загрузка…</p></div>
      ) : filtered.length === 0 ? (
        <div className="rf-empty rf-in">
          <div className="rf-empty-ic">{query ? <Search /> : showArchived ? <Archive /> : <Sparkles />}</div>
          <h3>{query ? 'Ничего не найдено' : showArchived ? 'Архив пуст' : 'Начните диалог'}</h3>
          <p>{query ? 'Попробуйте другой запрос' : showArchived ? 'Здесь появятся архивированные беседы' : 'Нажмите «+» — опишите задачу словами'}</p>
        </div>
      ) : (
        <div className="rf-clist rf-in">
          {filtered.map((c: any) => (
            <div key={c.id} className="rf-citem">
              {renaming === c.id ? (
                <div className="rf-citem-rename">
                  <input className="rf-input" autoFocus value={renameVal}
                    onChange={(e) => setRenameVal(e.target.value)} placeholder="Название диалога"
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }} />
                  <button className="rf-btn primary icon" onClick={saveRename} disabled={savingRename}>{savingRename ? <Loader2 className="spin" /> : <Check />}</button>
                  <button className="rf-btn icon" onClick={() => setRenaming(null)}><X /></button>
                </div>
              ) : (
                <div className="rf-citem-row" onClick={() => { haptic('light'); setActiveId(c.id); }}>
                  <div className="rf-citem-av" style={{ background: avatarGrad(c.title || '') }}>{initial(c.title || 'Д')}</div>
                  <div className="rf-citem-body">
                    <div className="rf-citem-title">{c.title || 'Диалог'}</div>
                    <div className="rf-citem-preview">
                      {c.lastMessage ? String(c.lastMessage).replace(/\n+/g, ' ') : <span className="rf-citem-empty">Нет сообщений</span>}
                    </div>
                    <div className="rf-citem-time">{relTime(c.updatedAt || c.createdAt || '')}</div>
                  </div>
                  {/* Видимая кнопка «Переименовать» + меню «⋯» */}
                  <button className="rf-citem-icon" title="Переименовать" onClick={(e) => { e.stopPropagation(); startRename(c); }}><Edit3 /></button>
                  <div className="rf-citem-menuwrap" onClick={(e) => e.stopPropagation()}>
                    <button className="rf-citem-icon" title="Ещё" onClick={() => { haptic('light'); setMenuFor(menuFor === c.id ? null : c.id); }}><MoreVertical /></button>
                    {menuFor === c.id && (
                      <div className="rf-citem-menu">
                        <button onClick={() => archive(c.id)}>
                          {showArchived ? <ArchiveRestore /> : <Archive />}{showArchived ? 'Вернуть' : 'В архив'}
                        </button>
                        <button className="danger" onClick={() => del(c.id)}><Trash2 />Удалить</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button className="rf-fab" onClick={newChat} aria-label="Новый диалог"><Plus /></button>

      {toast && (
        <div className="rf-toast" role="status">
          <span className="rf-toast-text">{toast.text}</span>
          {toast.onUndo && (
            <button className="rf-toast-undo" onClick={async () => { const fn = toast.onUndo; hideToast(); if (fn) await fn(); }}><Undo2 />Отменить</button>
          )}
        </div>
      )}
    </div>
  );
}
